import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaDownload,
  FaFileWord,
  FaRedo,
  FaTachometerAlt,
  FaChevronRight,
  FaChevronLeft,
  FaFileAlt,
  FaBriefcase,
  FaCheckCircle,
  FaTimes,
  FaHome
} from 'react-icons/fa';
import './EnhancedResultsView.css';

const EnhancedResultsView = ({
  data,
  jobTitle,
  company,
  onDownloadPdf,
  onDownloadDocx,
  onRegenerate,
  onDashboard
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // If data is not available yet
  if (!data) {
    return <div className="results-loading">Loading your tailored resume...</div>;
  }

  // Extract data from response
  const { job_analysis, tailored_profile, match_score } = data;

  // Format match score as percentage
  const formatScore = (score) => {
    return `${Math.round(score * 100)}%`;
  };

  // Get color class based on match score
  const getScoreClass = (score) => {
    if (score >= 0.8) return 'score-high';
    if (score >= 0.6) return 'score-medium';
    return 'score-low';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';

    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="enhanced-results-view">
      <div className="results-header">
        <h2>
          {jobTitle && company
            ? `Tailored Resume for ${jobTitle} at ${company}`
            : jobTitle
              ? `Tailored Resume for ${jobTitle}`
              : company
                ? `Tailored Resume for ${company}`
                : 'Your Tailored Resume'}
        </h2>

        <div className="results-actions">
          {/* <button className="action-button" onClick={onDownloadPdf}>
            <FaDownload /> Download PDF
          </button> */}
          <button className="action-button" onClick={onDownloadDocx}>
            <FaFileWord /> Download DOCX
          </button>
          <button className="action-button" onClick={onRegenerate}>
            <FaRedo /> Regenerate
          </button>
          <button className="action-button" onClick={onDashboard}>
            <FaHome /> Dashboard
          </button>
        </div>
      </div>

      <div className="match-score-banner">
        <div className="match-score-container">
          <div className="match-score-label">
            <FaTachometerAlt /> Resume Match Score
          </div>
          <div className={`match-score-value ${getScoreClass(match_score)}`}>
            {formatScore(match_score)}
          </div>
        </div>
      </div>

      <div className="results-content-container">
        <div className={`job-analysis-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {/* <div className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </div> */}

          <div className="sidebar-content">
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <FaBriefcase /> Job Analysis
              </h3>

              {job_analysis.company_info && (
                <div className="sidebar-subsection">
                  <h4>About the Company</h4>
                  <p>{job_analysis.company_info}</p>
                </div>
              )}

              <div className="sidebar-subsection">
                <h4>Key Skills</h4>
                <div className="skills-list">
                  {job_analysis.key_skills.map((skill, index) => (
                    <div key={index} className="skill-item">
                      <div className="skill-name">
                        {skill.name}
                      </div>
                      <div className="skill-status">
                        {skill.is_required && (
                          <span className="required-badge">Required</span>
                        )}
                        {/* {skill.is_matched ? (
                          <FaCheckCircle className="match-icon" />
                        ) : (
                          <FaTimes className="mismatch-icon" />
                        )} */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sidebar-subsection">
                <h4>Key Requirements</h4>
                <ul className="requirements-list">
                  {job_analysis.key_requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>

              <div className="sidebar-subsection">
                <h4>Major Responsibilities</h4>
                <ul className="responsibilities-list">
                  {job_analysis.major_responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="resume-preview">
          <div className="resume-document">
            <div className="resume-header-section">
              <h1 className="resume-name">
                {tailored_profile.personal_information?.first_name} {tailored_profile.personal_information?.last_name}
              </h1>

              <div className="resume-contact-info">
                {tailored_profile.personal_information?.email && (
                  <div className="contact-item">
                    {tailored_profile.personal_information.email}
                  </div>
                )}
                {tailored_profile.personal_information?.phone_number && (
                  <div className="contact-item">
                    {tailored_profile.personal_information.phone_number}
                  </div>
                )}
                {tailored_profile.personal_information?.location && (
                  <div className="contact-item">
                    {tailored_profile.personal_information.location}
                  </div>
                )}
                {tailored_profile.linkedin && (
                  <div className="contact-item">
                    {tailored_profile.linkedin}
                  </div>
                )}
              </div>
            </div>

            {tailored_profile.summary && (
              <div className="resume-section">
                <h2 className="section-title">Professional Summary</h2>
                <div className="section-content">
                  <p>{tailored_profile.summary}</p>
                </div>
              </div>
            )}

            {tailored_profile.experiences && tailored_profile.experiences.length > 0 && (
              <div className="resume-section">
                <h2 className="section-title">Professional Experience</h2>
                <div className="section-content">
                  {tailored_profile.experiences.map((exp, index) => (
                    <div key={index} className="experience-item">
                      <div className="item-header">
                        <div className="item-title-group">
                          <h3 className="item-title">{exp.job_title || exp.position || exp.title}</h3>
                          <div className="item-subtitle">{exp.company_name || exp.company}</div>
                        </div>
                        <div className="item-period">
                          {formatDate(exp?.period?.start_date)} – {exp.is_current ? 'Present' : formatDate(exp?.period?.end_date) || 'Present'}
                        </div>
                      </div>
                      <ul className="item-details">
                        {exp?.description && exp?.description.map((resp, respIndex) => (
                          <li key={respIndex}>{resp}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tailored_profile.education && tailored_profile.education.length > 0 && (
              <div className="resume-section">
                <h2 className="section-title">Education</h2>
                <div className="section-content">
                  {tailored_profile.education.map((edu, index) => (
                    <div key={index} className="education-item">
                      <div className="item-header">
                        <div className="item-title-group">
                          <h3 className="item-title">{edu.degree} {edu.field_of_study && `in ${edu.field_of_study}`}</h3>
                          <div className="item-subtitle">{edu.institution_name}</div>
                        </div>
                        <div className="item-period">
                          {formatDate(edu.start_date)} – {formatDate(edu.end_date)}
                        </div>
                      </div>
                      {edu.gpa && (
                        <div className="education-details">
                          <span className="gpa">GPA: {edu.gpa}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tailored_profile?.skills && (
              <div className="resume-section">
                <h2 className="section-title">Skills</h2>
                <div className="section-content">
                  <div className="skills-grid">
                    {Object.entries(tailored_profile.skills)
                      .filter(([key]) => key !== "match_score")
                      .map(([category, skills], catIndex) => (
                        <div key={catIndex} className="skill-category">
                          <h3 className="category-name">{category}</h3>
                          <div className="skills-container">
                            {skills.map((skill, skillIndex) => (
                              <span key={skillIndex} className="skill-tag">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {tailored_profile?.projects && tailored_profile?.projects?.length > 0 && (
              <div className="resume-section">
                <h2 className="section-title">Projects</h2>
                <div className="section-content">
                  {tailored_profile.projects.map((project, index) => (
                    <div key={index} className="project-item">
                      <div className="item-header">
                        <div className="item-title-group">
                          <h3 className="item-title">{project?.project_title || project?.name}</h3>
                        </div>
                        {(project?.period?.start_date || project?.period?.end_date) && (
                          <div className="item-period">
                            {formatDate(project?.period?.start_date)} – {project?.period?.is_current ? 'Present' : formatDate(project?.period?.end_date)||'Present'}
                          </div>
                        )}
                      </div>

                      <ul className="item-details">
                        {project?.description && project?.description.map((resp, respIndex) => (
                          <li key={respIndex}>{resp}</li>
                        ))}
                      </ul>

                      {project?.technologies && project?.technologies.length > 0 && (
                        <div className="project-technologies">
                          {project.technologies.map((tech, techIndex) => (
                            <span key={techIndex} className="tech-tag">
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tailored_profile.certificates && tailored_profile.certificates.length > 0 && (
              <div className="resume-section">
                <h2 className="section-title">Certifications</h2>
                <div className="section-content">
                  <div className="certifications-grid">
                    {tailored_profile.certificates.map((cert, index) => (
                      <div key={index} className="certification-item">
                        <h3 className="certification-name">{cert.certificate_name}</h3>
                        <div className="certification-issuer">{cert.issuing_organization}</div>
                        <div className="certification-date">
                          {formatDate(cert.issue_date)}
                          {cert.expiration_date && ` - ${formatDate(cert.expiration_date)}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tailored_profile.extracurricular_activities && tailored_profile.extracurricular_activities.length > 0 && (
              <div className="resume-section">
                <h2 className="section-title">Extracurricular Activities</h2>
                <div className="section-content">
                  {tailored_profile.extracurricular_activities.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="item-header">
                        <div className="item-title-group">
                          <h3 className="item-title">{activity.activity_name}</h3>
                          {activity.organization && (
                            <div className="item-subtitle">{activity.organization}</div>
                          )}
                          {activity.role && (
                            <div className="item-role">{activity.role}</div>
                          )}
                        </div>
                        {(activity.start_date || activity.end_date) && (
                          <div className="item-period">
                            {formatDate(activity.start_date)} – {activity.end_date ? formatDate(activity.end_date) : 'Present'}
                          </div>
                        )}
                      </div>
                      {activity.description && (
                        <p className="activity-description">{activity.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tailored_profile.custom_sections && tailored_profile.custom_sections.length > 0 && (
              <>
                {tailored_profile.custom_sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="resume-section">
                    <h2 className="section-title">{section.section_name}</h2>
                    <div className="section-content">
                      {section.items && section.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="custom-section-item">
                          <div className="item-header">
                            <div className="item-title-group">
                              {item.title && <h3 className="item-title">{item.title}</h3>}
                              {item.subtitle && <div className="item-subtitle">{item.subtitle}</div>}
                            </div>
                            {(item.start_date || item.end_date) && (
                              <div className="item-period">
                                {formatDate(item.start_date)} – {item.end_date ? formatDate(item.end_date) : 'Present'}
                              </div>
                            )}
                          </div>
                          {item.description && (
                            <p className="item-description">{item.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EnhancedResultsView.propTypes = {
  data: PropTypes.object,
  jobTitle: PropTypes.string,
  company: PropTypes.string,
  onDownloadPdf: PropTypes.func.isRequired,
  onDownloadDocx: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func.isRequired,
  onDashboard: PropTypes.func.isRequired
};

export default EnhancedResultsView;